import { CiEdit } from "react-icons/ci"
import { useContext, useEffect, useState } from "react";
import { RxLockClosed, RxLockOpen2, RxPlusCircled } from "react-icons/rx";
import { SetupElementEdit, StyleSelectScreen } from "../../../utils/setupUtils";
import { categoryColors } from "../../../entries";
import { loadingActive, pageLoadingActive } from "../../../components/Background";
import { requestCategories, requestCategoryTicketNumbers, requestDeleteCategory, requestDisableCategory, requestEditCategory, requestEnableCategory, requestSubmitNewCategory } from "../../../requests/setup_requests";
import { ContentContext } from "../../../contexts/ContentContext";
import { PageHeader } from "../../../components/HeaderComponents";
import { useTranslation } from "react-i18next";
import { PermissionContext } from "../../../contexts/PermissionContexts";
import { useNavigate } from "react-router-dom";
import { MdClose, MdDone } from "react-icons/md";
import { useConfirm } from "../../../contexts/ModalContexts";
import { IoBanOutline } from "react-icons/io5";
import { t } from "i18next";
import { CategorySetupElementEdit } from "../../../components/CategorySetupComponents";


export const CategorySetup = () => {
    const { t } = useTranslation('settings')
    const { openErrorModal } = useContext(ContentContext)
    const { hasPermission } = useContext(PermissionContext)

    const [editInProgress, setEditInProgress] = useState(false);
    const [openNew, setOpenNew] = useState(false)
    const { categories, _setCategories } = useContext(ContentContext)
    const [ticketNumbers, setTicketNumbers] = useState({})
    const [categoryNumbers, setCategoryNumbers] = useState({})

    const navigator = useNavigate()

    const handleOpenNew = () => {
        setOpenNew(true)
    }

    useEffect(() => {

        if (!hasPermission("setup:view")) {
            navigator("/admin/dashboard")
            return
        }

        pageLoadingActive(true)

        requestCategoryTicketNumbers({})
            .then((data) => {
                setTicketNumbers(data.ticket_numbers)
                setCategoryNumbers(data.category_numbers)
            })
            .catch((error) => {
                console.error(error.status, error.message)
                openErrorModal(error)
            })
        requestCategories({})
            .then((data) => {
                _setCategories(data.categories)
                pageLoadingActive(false)
            })
            .catch((error) => {
                console.error(error.status, error.message)
                openErrorModal(error)
                pageLoadingActive(false)
            })
    }, [])

    useEffect(() => {
        if (openNew) {
            setEditInProgress(true)
        }
        else {
            setEditInProgress(false)
        }
    }, [openNew])


    if (!hasPermission("setup:view")) {
        return <></>
    }

    return (
        <div className="flex flex-col w-full bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-300 border-gray-300 dark:border-gray-600 rounded-md px-4 py-3 gap-3">
            <PageHeader pageTitle={t("Category Setup")} />
            <div className="flex flex-col">
                <span>{t('Set up the category parameters.')}</span>
                {/* <span className="text-red-600">{t('After setting up or modifying the categories, please consult the site administrator for translations setup.')}</span> */}
            </div>
            <div className="grid grid-cols-6 w-full gap-3 items-center ">
                <span className="label-text text-base text-gray-800 dark:text-gray-300 font-semibold">
                    {t('Title')}
                </span>
                <span className="label-text text-base text-gray-800 dark:text-gray-300 font-semibold">
                    {t('Value')}
                </span>
                <span className="label-text text-base text-gray-800 dark:text-gray-300 font-semibold">
                    {t('Tickets')}
                </span>
                <span className="label-text text-base text-gray-800 dark:text-gray-300 font-semibold">
                    {t('Assignees')}
                </span>
                <span className="label-text text-base text-gray-800 dark:text-gray-300 font-semibold">
                    {t('Disabled')}
                </span>
            </div>

            {
                Object.values(categories).map((category, index) =>
                    <CategoryElement key={category.id} props={category} ticketNumbers={ticketNumbers} categoryNumbers={categoryNumbers} setCategories={_setCategories} editInProgress={editInProgress} setEditInProgress={setEditInProgress} />
                )
            }

            {
                !openNew ?
                    <div className="flex w-full justify-center mt-4">
                        {
                            hasPermission("setup:create") &&
                            <button className="btn w-full btn-md h-9 p-0 text-primary dark:text-blue-600 bg-white dark:bg-gray-700 hover:text-white hover:bg-primary  border-gray-500 dark:border-gray-500 hover:border-gray-300 dark:hover:border-gray-500 "
                                disabled={editInProgress}
                                onClick={handleOpenNew}
                            >
                                <RxPlusCircled size={30} />
                            </button>
                        }
                    </div>
                    :
                    <CategorySetupElementEdit
                        key={"$$$$$$$$$"}
                        props
                        setOpen={setOpenNew}
                        setElement={_setCategories}
                        edit={false}
                        placeholder="Category"
                        styles={categoryColors}
                        requestNewElement={requestSubmitNewCategory}
                        requestEditElement={requestEditCategory}
                        requestDeleteElement={requestDeleteCategory}
                    />
            }
        </div>
    )
}


const CategoryElement = ({ props, setCategories, ticketNumbers, categoryNumbers, editInProgress, setEditInProgress }) => {
    const { t } = useTranslation('settings')
    const { hasPermission } = useContext(PermissionContext)
    const { openErrorModal } = useContext(ContentContext)
    const confirm = useConfirm()

    const [edit, setEdit] = useState(false)
    const [newElements, setNewElements] = useState(null)

    const handleOpenEdit = () => {
        setEdit(true)
    }

    useEffect(() => {
        if (edit) {
            setEditInProgress(true)
        }
        else {
            setEditInProgress(false)
        }
    }, [edit])

    useEffect(() => {
        if (newElements !== null) {
            setCategories(newElements)
            setNewElements(null)
        }
    }, [newElements])


    const handleDisableCategory = async () => {
        const confirmationMessage = props.disabled ? t("disableConfirmation.enable") : t("disableConfirmation.disable")
        const isConfirmed = await confirm(confirmationMessage)
        if (isConfirmed) {
            loadingActive(true)
            if (props.disabled) {
                requestEnableCategory({ id: props.id })
                    .then((data) => {
                        setNewElements(data)
                    })
                    .catch((error) => {
                        console.error(error.status, error.message)
                        openErrorModal(error)
                    })
                    .finally(() => {
                        loadingActive(false)
                    })
            }
            else {
                requestDisableCategory({ id: props.id })
                    .then((data) => {
                        setNewElements(data)
                    })
                    .catch((error) => {
                        console.error(error.status, error.message)
                        openErrorModal(error)
                    })
                    .finally(() => {
                        loadingActive(false)
                    })
            }
        }
    }

    return (
        !edit ?
            <div className={`grid grid-cols-6 w-full rounded-sm gap-3 items-center group  text-gray-800 dark:text-gray-300 ${categoryNumbers[props.id] ? "bg-inherit hover:bg-gray-100 dark:hover:bg-gray-600" : "bg-red-100 dark:bg-red-900/50 hover:bg-red-200 dark:hover:bg-red-800/50"} py-1`}>
                <div className={`${props.style} rounded-full select-none px-[6px] min-w-16 p-1 max-w-max text-center truncate text-nowrap`}>
                    <span>{props.title}</span>
                </div>
                <span>{props.value}</span>
                <span className={`${ticketNumbers[props.id] ? "" : "text-gray-600/50 dark:text-gray-400/50"}`}>{ticketNumbers[props.id] ? ticketNumbers[props.id] : 0}</span>
                <span className={`${categoryNumbers[props.id] ? "" : "text-red-600 font-bold"}`}>{categoryNumbers[props.id] ? categoryNumbers[props.id] : 0}</span>
                <span className="">{props.disabled ? <RxLockClosed size={16} /> : ""}</span>
                <div className="flex items-center gap-2">
                    {
                        hasPermission("category:disable") &&
                        <button className="btn btn-sm w-8 opacity-0 group-hover:opacity-100 transition-all duration-300 disabled:invisible ease-in-out border-none bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-300 hover:bg-primary dark:hover:bg-blue-600 hover:text-white p-0"
                            title={
                                props.disabled ? t("Enable") : t("Disable")
                            }
                            disabled={editInProgress}
                            onClick={handleDisableCategory}
                        >
                            {
                                props.disabled
                                    ?
                                    <RxLockOpen2 size={18} />
                                    :
                                    <RxLockClosed size={18} />
                            }
                        </button>
                    }
                    {
                        hasPermission("setup:edit") &&
                        <button className="btn btn-sm w-8 opacity-0 group-hover:opacity-100 transition-all duration-300 disabled:invisible ease-in-out border-none bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-300 hover:bg-primary dark:hover:bg-blue-600 hover:text-white p-0"
                            disabled={editInProgress}
                            onClick={handleOpenEdit}
                        >
                            <CiEdit size={24} />
                        </button>
                    }
                </div>
            </div >
            :
            <CategorySetupElementEdit
                key={props.id}
                props={props}
                setOpen={setEdit}
                setElement={setNewElements}
                edit={true}
                isDeletable={ticketNumbers[props.id] ? ticketNumbers[props.id] === 0 : true}
                placeholder={"Category"}
                styles={categoryColors}
                requestNewElement={requestSubmitNewCategory}
                requestEditElement={requestEditCategory}
                requestDeleteElement={requestDeleteCategory}
            />

    )
}


